import type {
    DmLastResolution,
    DmNotification,
    DmNotificationResolution,
    DmNotificationReviewer,
    DmResolvedNotification
} from '../../../../services/dmNotifications';
import { compactName } from '../../../conversations/assigneeSelectorHelpers.ts';

// What a closure reads from: a resolved notification, or a patient's lastResolution.
export type ClosureSource = Pick<
    DmLastResolution,
    'resolution' | 'resolvedInDm' | 'resolvedByName'
>;

type ResolutionLabel = (source: ClosureSource) => string;

const FALLBACK_RESOLUTION_LABEL = 'Resolved';
const SEPARATOR = ' · ';

function reviewerName(reviewer: DmNotificationReviewer): string {
    return reviewer.fullname?.trim() || reviewer.email;
}

const RESOLUTION_LABELS: Readonly<Record<DmNotificationResolution, ResolutionLabel>> = {
    dashboard: ({ resolvedByName }) =>
        resolvedByName ? `Reviewed by ${compactName(resolvedByName)}` : 'Reviewed in dashboard',
    dm: () => 'Resolved in DM',
    gone: () => 'No longer in DM',
    excluded: () => 'No longer applicable'
};

// Read as a plain string map so a resolution the server adds later falls back instead of crashing.
const LABELS_BY_KEY: Readonly<Partial<Record<string, ResolutionLabel>>> = RESOLUTION_LABELS;

export function isResolvedNotification(
    notification: DmNotification
): notification is DmResolvedNotification {
    return Boolean(notification.resolvedAt && notification.resolution);
}

export function notificationClosure(notification: DmResolvedNotification): ClosureSource {
    const { resolution, resolvedInDm, resolvedBy } = notification;
    return { resolution, resolvedInDm, resolvedByName: resolvedBy && reviewerName(resolvedBy) };
}

export function resolutionLabel(source: ClosureSource): string {
    const label = LABELS_BY_KEY[source.resolution];
    return label ? label(source) : FALLBACK_RESOLUTION_LABEL;
}

export function resolutionSuffix(source: ClosureSource): string | null {
    return source.resolution === 'dashboard' && source.resolvedInDm ? 'DM updated' : null;
}

// Label and suffix on one line, for a pill's tooltip or a narrow cell.
export function closureText(source: ClosureSource): string {
    const suffix = resolutionSuffix(source);
    return suffix ? `${resolutionLabel(source)}${SEPARATOR}${suffix}` : resolutionLabel(source);
}
